import type { Suggestions } from '@mint-sdk/api-recommendations';
import { lightFormat, sub } from 'date-fns';
import { atomWithReducer } from 'jotai/utils';

import type { SuggestionVersion } from '../../hooks/useSuggestionVersion.js';

export enum SuggestionFilterActions {
  SET_DATE = 'date',
  SET_CAMPAIGNS = 'campaigns',
  SET_PLATFORMS = 'platforms',
  SET_CHANNELS = 'channels',
  SET_CAMPAIGN_NAME = 'campaignName',
  SET_VERSION = 'version',
  SET_CAMPAIGN_ID = 'campaignId',
  RESET = 'reset',
}

export type SuggestionAction =
  | {
      type: SuggestionFilterActions.SET_DATE;
      data: { dateFrom: string; dateTo: string };
    }
  | {
      type:
        | SuggestionFilterActions.SET_CHANNELS
        | SuggestionFilterActions.SET_CAMPAIGNS
        | SuggestionFilterActions.SET_PLATFORMS;
      data: string[];
    }
  | { type: SuggestionFilterActions.SET_CAMPAIGN_NAME; data: string }
  | { type: SuggestionFilterActions.RESET; data: string }
  | { type: SuggestionFilterActions.SET_VERSION; data: SuggestionVersion }
  | { type: SuggestionFilterActions.SET_CAMPAIGN_ID; data: number | undefined };

export const initialSuggestionDateRange = {
  from: lightFormat(sub(new Date(), { days: 90 }), 'yyyy-MM-dd'),
  to: lightFormat(new Date(), 'yyyy-MM-dd'),
};

export const initialSuggestionState: Suggestions.GetFilterRequestType = {
  dateFrom: initialSuggestionDateRange.from,
  dateTo: initialSuggestionDateRange.to,
  campaigns: [],
  platforms: [],
  channels: [],
  sortBy: 'date',
  statuses: 'ACTIVE,REWORKING',
  campaignName: '',
};

// FYI: Campaigns, platforms and channels must be provided to the query as a string
// { platforms: ["a,b,c,d"] }

const formatFilter = (data: string[]): string[] => {
  if (!data?.length) return [];
  return [data.join(',')];
};

const suggestionSelectedFiltersReducer = (
  prev: Suggestions.GetFilterRequestType,
  action: SuggestionAction,
) => {
  switch (action.type) {
    case SuggestionFilterActions.SET_DATE: {
      return { ...prev, ...action.data };
    }
    case SuggestionFilterActions.SET_PLATFORMS: {
      return { ...prev, platforms: formatFilter(action.data) };
    }
    case SuggestionFilterActions.SET_CAMPAIGNS: {
      return { ...prev, campaigns: formatFilter(action.data) };
    }
    case SuggestionFilterActions.SET_CHANNELS: {
      return { ...prev, channels: formatFilter(action.data) };
    }
    case SuggestionFilterActions.SET_CAMPAIGN_NAME: {
      return { ...prev, campaignName: action.data };
    }
    case SuggestionFilterActions.SET_VERSION: {
      return { ...prev, version: action.data };
    }
    case SuggestionFilterActions.SET_CAMPAIGN_ID: {
      return { ...prev, campaignId: action.data };
    }
    case SuggestionFilterActions.RESET: {
      return {
        ...initialSuggestionState,
        statuses: action.data,
        dateFrom: prev.dateFrom,
        dateTo: prev.dateTo,
        campaignId: prev.campaignId,
        version: prev.version,
      };
    }
    default:
      throw new Error('Unknown action type');
  }
};

export const suggestionSelectedFiltersAtom = atomWithReducer(
  initialSuggestionState,
  suggestionSelectedFiltersReducer,
);
